import { useEffect, useState } from 'react';
import { List } from 'lucide-react';

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[čć]/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z').replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
}

export default function ArticleTOC({ bodyRef }) {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [openMobile, setOpenMobile] = useState(false);

  useEffect(() => {
    const el = bodyRef?.current;
    if (!el) return;
    const headings = Array.from(el.querySelectorAll('h2'));
    if (headings.length < 3) { setItems([]); return; }
    const list = headings.map((h, i) => {
      const id = h.id || `h2-${slugify(h.textContent)}-${i}`;
      if (!h.id) h.id = id;
      h.style.scrollMarginTop = '80px';
      return { id, text: h.textContent };
    });
    setItems(list);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.target.offsetTop - b.target.offsetTop)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    headings.forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [bodyRef]);

  if (items.length === 0) return null;

  return (
    <>
      {/* Mobile collapsible */}
      <div className="lg:hidden mb-4 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden" data-testid="article-toc-mobile">
        <button onClick={() => setOpenMobile(o => !o)} className="w-full px-4 py-2 flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#111827]">
          <List size={14} /> Sadržaj članka
        </button>
        {openMobile && (
          <div className="p-3 space-y-1 bg-white dark:bg-[#0f172a]">
            {items.map(it => (
              <a key={it.id} href={`#${it.id}`} className={`block text-sm px-2 py-1 rounded ${activeId === it.id ? 'text-red-600 font-medium' : 'text-gray-700 dark:text-gray-300 hover:text-red-600'}`}>
                {it.text}
              </a>
            ))}
          </div>
        )}
      </div>
      {/* Desktop sticky — floats to the right of content */}
      <aside className="hidden lg:block fixed right-6 top-28 z-10 w-56 xl:w-64" data-testid="article-toc-desktop">
        <div className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm max-h-[70vh] overflow-y-auto">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5"><List size={12} /> Sadržaj</p>
          <ul className="space-y-1 text-sm">
            {items.map(it => (
              <li key={it.id}>
                <a href={`#${it.id}`} className={`block px-2 py-1 rounded border-l-2 transition-colors ${activeId === it.id ? 'border-red-600 text-red-600 bg-red-50 dark:bg-red-900/10' : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-red-600 hover:border-red-200'}`}>
                  {it.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
}
